import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { Profile, UserRole } from '../types/profile';
import { useWindowFocus } from '../hooks/useWindowFocus';
import { useDatabaseMigrations } from '../hooks/useDatabaseMigrations';
import { persistentSessionManager } from '../utils/persistentSessionManager';
import { sessionManager } from '../utils/sessionManager';
// import { setupEventListenerDebug } from '../utils/debugEventListeners';
// import { trackSessionInvalidation } from '../utils/sessionInvalidationTracker';

interface UserFirma {
  firma_id: string;
  firma_adi: string;
  role: string;
  is_active: boolean;
}

interface UserPermission {
  permission_code: string;
  permission_name: string;
  module_name: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userFirmas: UserFirma[];
  currentFirma: UserFirma | null;
  permissions: UserPermission[];
  loading: boolean;
  hasPermission: (permissionCode: string) => boolean;
  switchFirma: (firmaId: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  tempPasswordWarning: {
    show: boolean;
    daysRemaining: number;
  };
  dismissTempPasswordWarning: () => void;
}

export type { UserFirma };

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const isDev = import.meta.env.DEV;
const debugLog = (...args: unknown[]) => {
  if (isDev) console.log(...args);
};
const debugWarn = (...args: unknown[]) => {
  if (isDev) console.warn(...args);
};
const debugTrace = (...args: unknown[]) => {
  if (isDev) console.debug(...args);
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Debug setup - devre dışı
  // useEffect(() => {
  //   setupEventListenerDebug();
  //   trackSessionInvalidation();
  // }, []);
  
  // Auto migrations
  const { migrationStatus } = useDatabaseMigrations();
  
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userFirmas, setUserFirmas] = useState<UserFirma[]>([]);
  const [currentFirma, setCurrentFirma] = useState<UserFirma | null>(null);
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [tempPasswordWarning, setTempPasswordWarning] = useState({ show: false, daysRemaining: 0 });
  const fetchingRef = useRef(false);
  const profileCache = useRef<Map<string, Profile>>(new Map());
  const lastFetchedUser = useRef<string | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  
  // Custom auth user'dan permissions çekilmesi
  useEffect(() => {
    const customAuthUser = localStorage.getItem('custom_auth_user');
    if (customAuthUser) {
      try {
        const user = JSON.parse(customAuthUser);
        if (user.permissions && Array.isArray(user.permissions)) {
          debugLog('custom_auth_user permissions loaded:', user.permissions.length);
          setPermissions(user.permissions);
        }
      } catch (e) {
        debugWarn('custom_auth_user parse error:', e);
      }
    }
  }, []);
  
  // Minimal session kontrolü - sayfa yenileme yok
  const handleWindowFocus = async () => {
    if (session?.user && !loading && !profile && !fetchingRef.current) {
      debugTrace('profile missing, fetching data');
      await fetchAllUserData(session.user.id, session.user.email, true);
    }
  };
  
  useWindowFocus(handleWindowFocus);
  
  const fetchAllUserData = async (userId: string, userEmail?: string, isRetry = false) => {
    if (fetchingRef.current && !isRetry) return;
    fetchingRef.current = true;
    lastFetchedUser.current = userId;
    
    // İlk olarak cache'den emergency profile oluştur
    const lastKnownRole = localStorage.getItem(`lastRole_${userId}`);
    const lastKnownFirmas = localStorage.getItem(`lastFirmas_${userId}`);
    
    if (lastKnownRole) {
      const emergencyProfile = {
        id: userId,
        email: userEmail,
        tam_adi: userEmail || 'User',
        rol: lastKnownRole as any,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      setProfile(emergencyProfile);
      
      if (lastKnownFirmas) {
        try {
          const firmas = JSON.parse(lastKnownFirmas);
          setUserFirmas(firmas);
          if (firmas.length > 0) {
            setCurrentFirma(firmas[0]);
            setPermissions(getFallbackPermissions(firmas[0].role));
          } else {
            setCurrentFirma(null);
            setPermissions(getFallbackPermissions(lastKnownRole));
          }
        } catch {
          setUserFirmas([]);
          setCurrentFirma(null);
          setPermissions(getFallbackPermissions(lastKnownRole));
        }
      } else {
        setUserFirmas([]);
        setCurrentFirma(null);
        setPermissions(getFallbackPermissions(lastKnownRole));
      }
      
      setLoading(false);
      fetchingRef.current = false;
      return;
    }
    
    // Hızlı loading timeout - 1 saniye
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => {
      debugWarn('auth loading timeout, creating emergency profile');
      fetchingRef.current = false;
      
      const emergencyRole = userEmail?.includes('admin') ? 'firma_super_admin' as const : 'bolum_kullanicisi' as const;
      const emergencyProfile = {
        id: userId,
        email: userEmail,
        tam_adi: userEmail || 'Emergency User',
        rol: emergencyRole as any,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      setProfile(emergencyProfile);
      setUserFirmas([]);
      setCurrentFirma(null);
      setPermissions(getFallbackPermissions(emergencyRole));
      setLoading(false);
    }, 1000);
    
    try {
      // Check cache first
      const cachedProfile = profileCache.current.get(userId);
      if (cachedProfile) {
        setProfile(cachedProfile);
        // Cache'den gelen profil rolünü de kaydet
        localStorage.setItem(`lastRole_${userId}`, cachedProfile.rol);
        await fetchUserFirmas(userId);
        return;
      }

      // Fetch profile with longer timeout
      const { data: existingProfile, error } = await Promise.race([
        supabase
          .from('profiles')
          .select('id, email, tam_adi, rol, telefon, created_at, updated_at, temp_password, is_demo, demo_role, demo_starts_at, demo_expires_at, demo_status, demo_plan_type, demo_notes, sales_owner_user_id')
          .eq('id', userId)
          .single(),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Profile query timeout')), 800)
        )
      ]);
      
      if (!error && existingProfile) {
        profileCache.current.set(userId, existingProfile);
      setProfile(existingProfile as Profile);
        // Profil rolünü localStorage'a kaydet
        localStorage.setItem(`lastRole_${userId}`, existingProfile.rol);
        await fetchUserFirmas(userId);
        return;
      }
      
      // Fallback profile - son bilinen rolü kullan
      const lastKnownRole = localStorage.getItem(`lastRole_${userId}`);
      const fallbackProfile: Profile = {
        id: userId,
        email: userEmail,
        tam_adi: userEmail || 'User',
        rol: (lastKnownRole || (userEmail?.includes('admin') ? 'firma_super_admin' : 'bolum_kullanicisi')) as UserRole,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      setProfile(fallbackProfile);
      await fetchUserFirmas(userId);
      
    } catch (error) {
      console.error('Auth data fetch error:', error);
      
      // Retry logic
      if (retryCountRef.current < maxRetries && !isRetry) {
        debugTrace(`Retrying fetch (${retryCountRef.current + 1}/${maxRetries})`);
        fetchingRef.current = false;
        await retryFetchUserData(userId, userEmail);
        return;
      }
      
      // Emergency profile after max retries - son bilinen rolü kullan
      const lastKnownRole = localStorage.getItem(`lastRole_${userId}`);
      const emergencyProfile: Profile = {
        id: userId,
        email: userEmail,
        tam_adi: userEmail || 'Emergency User',
        rol: (lastKnownRole || 'firma_super_admin') as UserRole,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      setProfile(emergencyProfile);
      
      // Son bilinen firma bilgilerini de yükle
      const lastKnownFirmas = localStorage.getItem(`lastFirmas_${userId}`);
      if (lastKnownFirmas) {
        try {
          const firmas = JSON.parse(lastKnownFirmas);
          setUserFirmas(firmas);
          if (firmas.length > 0) {
            setCurrentFirma(firmas[0]);
            setPermissions(getFallbackPermissions(firmas[0].role));
          } else {
            setCurrentFirma(null);
            setPermissions(getFallbackPermissions(lastKnownRole || 'firma_super_admin'));
          }
        } catch {
          setUserFirmas([]);
          setCurrentFirma(null);
          setPermissions(getFallbackPermissions(lastKnownRole || 'firma_super_admin'));
        }
      } else {
        setUserFirmas([]);
        setCurrentFirma(null);
        setPermissions(getFallbackPermissions(lastKnownRole || 'firma_super_admin'));
      }
    } finally {
      fetchingRef.current = false;
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      setLoading(false);
      retryCountRef.current = 0;
    }
  };

  const fetchUserFirmas = async (userId: string) => {
    try {
      debugTrace('fetchUserFirmas starting', userId);
      debugTrace('fetchUserFirmas profile role', profile?.rol);

      // Super admin için tüm firmalar
      if (profile?.rol === 'ekostrateji_super_admin') {
        debugTrace('fetchUserFirmas super admin detected');
        
        const { data: allFirmas, error: firmasError } = await supabase
          .from('firmalar')
          .select('id, firma_adi')
          .eq('is_active', true)
          .order('firma_adi');
        
        if (firmasError) {
          console.error('❌ fetchUserFirmas: Error fetching all firmalar:', firmasError);
          setUserFirmas([]);
          setCurrentFirma(null);
          setPermissions(getFallbackPermissions('ekostrateji_super_admin'));
          return;
        }

        const firmas = (allFirmas || []).map(firma => ({
          firma_id: firma.id,
          firma_adi: firma.firma_adi,
          role: 'ekostrateji_super_admin',
          is_active: true
        }));

        debugLog('fetchUserFirmas super admin firmas loaded:', firmas.length);
        
        setUserFirmas(firmas);
        
        // Firma bilgilerini localStorage'a kaydet
        localStorage.setItem(`lastFirmas_${userId}`, JSON.stringify(firmas));
        
        if (firmas.length > 0) {
          const defaultFirma = firmas[0];
          setCurrentFirma(defaultFirma);
          
          // Persistent session'ı güncelle
          persistentSessionManager.save({
            userFirmas: firmas,
            selectedFirma: {
              id: defaultFirma.firma_id,
              firma_adi: defaultFirma.firma_adi,
              label: defaultFirma.firma_adi
            },
            profile,
            permissions: getFallbackPermissions('ekostrateji_super_admin')
          });
          
          await fetchPermissions('ekostrateji_super_admin');
        } else {
          setCurrentFirma(null);
          setPermissions(getFallbackPermissions('ekostrateji_super_admin'));
        }
        return;
      }

      // Normal users - user_firma_relations'dan
      const { data, error } = await supabase
        .from('user_firma_relations')
        .select(`
          firma_id,
          role,
          is_active
        `)
        .eq('user_id', userId)
        .eq('is_active', true);

      if (error || !data || data.length === 0) {
        debugTrace('fetchUserFirmas no relations found, checking profile.firma_adi');
        
        // Firma Admin kullanıcılarının firma_adi'sinden firma bul
        if (profile?.firma_adi && (profile.rol === 'firma_admin' || profile.rol === 'firma_super_admin')) {
          debugTrace('fetchUserFirmas firma admin searching by firma_adi', profile.firma_adi);
          
          const { data: firmaByName, error: firmaByNameError } = await supabase
            .from('firmalar')
            .select('id, firma_adi')
            .eq('firma_adi', profile.firma_adi)
            .eq('is_active', true)
            .single();
          
          if (!firmaByNameError && firmaByName) {
            debugLog('fetchUserFirmas firma found by firma_adi', firmaByName.firma_adi);
            
            const firmas = [{
              firma_id: firmaByName.id,
              firma_adi: firmaByName.firma_adi,
              role: profile.rol,
              is_active: true
            }];
            
            setUserFirmas(firmas);
            localStorage.setItem(`lastFirmas_${userId}`, JSON.stringify(firmas));
            
            const defaultFirma = firmas[0];
            setCurrentFirma(defaultFirma);
            
            persistentSessionManager.save({
              userFirmas: firmas,
              selectedFirma: {
                id: defaultFirma.firma_id,
                firma_adi: defaultFirma.firma_adi,
                label: defaultFirma.firma_adi
              },
              profile,
              permissions: getFallbackPermissions(profile.rol)
            });
            
            await fetchPermissions(profile.rol);
            return;
          } else {
            debugWarn('fetchUserFirmas firma not found by firma_adi', profile.firma_adi);
          }
        }
        
        setUserFirmas([]);
        setCurrentFirma(null);
        setPermissions(getFallbackPermissions(profile?.rol || 'bolum_kullanicisi'));
        return;
      }

      const firmaIds = data.map(item => item.firma_id);
      const { data: firmaData, error: firmaError } = await supabase
        .from('firmalar')
        .select('id, firma_adi')
        .in('id', firmaIds);

      if (firmaError) {
        console.error('❌ fetchUserFirmas: Error fetching firmalar:', firmaError);
        setUserFirmas([]);
        setCurrentFirma(null);
        setPermissions(getFallbackPermissions(profile?.rol || 'bolum_kullanicisi'));
        return;
      }

      const firmas = data.map(item => {
        const firma = firmaData?.find(f => f.id === item.firma_id);
        return {
          firma_id: item.firma_id,
          firma_adi: firma?.firma_adi || 'Unknown Firma',
          role: item.role,
          is_active: item.is_active
        };
      });

      debugLog('fetchUserFirmas user firmas loaded:', firmas.length);
      
      setUserFirmas(firmas);
      
      // Firma bilgilerini localStorage'a kaydet
      localStorage.setItem(`lastFirmas_${userId}`, JSON.stringify(firmas));
      
      // Persistent session'ı güncelle
      persistentSessionManager.save({
        userFirmas: firmas,
        profile,
        permissions: getFallbackPermissions(profile?.rol || 'bolum_kullanicisi')
      });

      if (firmas.length > 0) {
        const defaultFirma = firmas[0];
        setCurrentFirma(defaultFirma);
        
        // Seçilen firmayı da persistent session'a kaydet
        persistentSessionManager.save({
          userFirmas: firmas,
          selectedFirma: {
            id: defaultFirma.firma_id,
            firma_adi: defaultFirma.firma_adi,
            label: defaultFirma.firma_adi
          },
          profile,
          permissions
        });
        
        await fetchPermissions(defaultFirma.role);
      } else {
        setCurrentFirma(null);
        setPermissions(getFallbackPermissions(profile?.rol || 'bolum_kullanicisi'));
      }
    } catch (error) {
      console.error('❌ User firmas fetch error:', error);
      
      // Fallback permissions based on profile role
      const fallbackRole = profile?.rol || 'bolum_kullanicisi';
      debugTrace('using fallback permissions for role', fallbackRole);
      
      setUserFirmas([]);
      setCurrentFirma(null);
      setPermissions(getFallbackPermissions(fallbackRole));
    }
  };

  const switchFirma = async (firmaId: string) => {
    const firma = userFirmas.find(f => f.firma_id === firmaId);
    if (firma) {
      setCurrentFirma(firma);
      await fetchPermissions(firma.role);
    }
  };

  const fetchPermissions = async (userRole: string) => {
    try {
      const { data, error } = await supabase
        .from('role_permissions')
        .select(`
          permission_code,
          permissions!inner(
            permission_name,
            module_name
          )
        `)
        .eq('role_code', userRole)
        .eq('is_active', true);

      if (error) {
        setPermissions(getFallbackPermissions(userRole));
        return;
      }

      const userPermissions = data?.map((item) => {
        const permission = Array.isArray(item.permissions) ? item.permissions[0] : item.permissions
        return {
          permission_code: item.permission_code,
          permission_name: permission?.permission_name,
          module_name: permission?.module_name,
        }
      }) || [];

      setPermissions(userPermissions);
    } catch (error) {
      console.error('Permission fetch error:', error);
      setPermissions(getFallbackPermissions(userRole));
    }
  };

  const getFallbackPermissions = (role: string): UserPermission[] => {
    switch (role) {
      case 'ekostrateji_super_admin':
        return [
          { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
          { permission_code: 'admin_panel_access', permission_name: 'Yönetim Paneli Erişimi', module_name: 'Yönetim' },
          { permission_code: 'user_management', permission_name: 'Kullanıcı Yönetimi', module_name: 'Yönetim' },
          { permission_code: 'company_management', permission_name: 'Firma Yönetimi', module_name: 'Yönetim' },
          { permission_code: 'topic_selection_view', permission_name: 'Konu Seçimi Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_selection_update', permission_name: 'Konu Seçimi Güncelleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_assignment_view', permission_name: 'Konu Atama Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'data_collection_update', permission_name: 'Veri Toplama Güncelleme', module_name: 'Veri Toplama' },
          { permission_code: 'sasb_index_view', permission_name: 'SASB İndex Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'survey_create', permission_name: 'Anket Oluşturma', module_name: 'Anket' },
          { permission_code: 'materiality_matrix_view', permission_name: 'Önemlilik Matrisi Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'tsrs_view', permission_name: 'TSRS Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_entities_view', permission_name: 'TSRS İşletmeler Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_skills_view', permission_name: 'TSRS Beceri Matrisi Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_valuechain_view', permission_name: 'TSRS Değer Zinciri Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_risks_view', permission_name: 'TSRS Riskler Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_opportunities_view', permission_name: 'TSRS Fırsatlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_emissions_view', permission_name: 'TSRS Emisyonlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_targets_view', permission_name: 'TSRS Hedefler Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_water_view', permission_name: 'TSRS Su Verileri Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_workforce_view', permission_name: 'TSRS İşgücü Verileri Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_safety_view', permission_name: 'TSRS İSG Verileri Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_goals_view', permission_name: 'TSRS Amaçlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_view', permission_name: 'GRI Raporlama Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_general_view', permission_name: 'GRI Genel Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_universal_view', permission_name: 'GRI Evrensel Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_economic_view', permission_name: 'GRI Ekonomik Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_environmental_view', permission_name: 'GRI Çevresel Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_social_view', permission_name: 'GRI Sosyal Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_analysis_view', permission_name: 'GRI Analiz Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'performance_indicators_view', permission_name: 'Performans Göstergeleri Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'reports_view', permission_name: 'Raporlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'reports_create', permission_name: 'Rapor Oluşturma', module_name: 'Raporlama' },
          { permission_code: 'knowledge_base_view', permission_name: 'Bilgi Bankası Görüntüleme', module_name: 'Bilgi' },
          { permission_code: 'profile_view', permission_name: 'Profil Görüntüleme', module_name: 'Profil' }
        ];
      case 'super_admin':
      case 'firma_super_admin':
        return [
          { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
          { permission_code: 'admin_panel_access', permission_name: 'Yönetim Paneli Erişimi', module_name: 'Yönetim' },
          { permission_code: 'topic_selection_view', permission_name: 'Konu Seçimi Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_assignment_view', permission_name: 'Konu Atama Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'sasb_index_view', permission_name: 'SASB İndex Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'survey_create', permission_name: 'Anket Oluşturma', module_name: 'Anket' },
          { permission_code: 'materiality_matrix_view', permission_name: 'Önemlilik Matrisi Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'tsrs_view', permission_name: 'TSRS Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'performance_indicators_view', permission_name: 'Performans Göstergeleri Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'reports_view', permission_name: 'Raporlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'knowledge_base_view', permission_name: 'Bilgi Bankası Görüntüleme', module_name: 'Bilgi' },
          { permission_code: 'profile_view', permission_name: 'Profil Görüntüleme', module_name: 'Profil' }
        ];
        
      case 'firma_admin':
        return [
          { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
          { permission_code: 'topic_selection_view', permission_name: 'Konu Seçimi Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_selection_update', permission_name: 'Konu Seçimi Güncelleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_assignment_view', permission_name: 'Konu Atama Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'data_collection_update', permission_name: 'Veri Toplama Güncelleme', module_name: 'Veri Toplama' },
          { permission_code: 'sasb_index_view', permission_name: 'SASB İndex Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'survey_create', permission_name: 'Anket Oluşturma', module_name: 'Anket' },
          { permission_code: 'materiality_matrix_view', permission_name: 'Önemlilik Matrisi Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'tsrs_view', permission_name: 'TSRS Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_entities_view', permission_name: 'TSRS İşletmeler Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_skills_view', permission_name: 'TSRS Beceri Matrisi Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_valuechain_view', permission_name: 'TSRS Değer Zinciri Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_risks_view', permission_name: 'TSRS Riskler Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_opportunities_view', permission_name: 'TSRS Fırsatlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_emissions_view', permission_name: 'TSRS Emisyonlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_targets_view', permission_name: 'TSRS Hedefler Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_water_view', permission_name: 'TSRS Su Verileri Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_workforce_view', permission_name: 'TSRS İşgücü Verileri Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_safety_view', permission_name: 'TSRS İSG Verileri Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'tsrs_goals_view', permission_name: 'TSRS Amaçlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_view', permission_name: 'GRI Raporlama Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_general_view', permission_name: 'GRI Genel Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_universal_view', permission_name: 'GRI Evrensel Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_economic_view', permission_name: 'GRI Ekonomik Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_environmental_view', permission_name: 'GRI Çevresel Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_social_view', permission_name: 'GRI Sosyal Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'gri_analysis_view', permission_name: 'GRI Analiz Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'performance_indicators_view', permission_name: 'Performans Göstergeleri Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'reports_view', permission_name: 'Raporlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'reports_create', permission_name: 'Rapor Oluşturma', module_name: 'Raporlama' },
          { permission_code: 'knowledge_base_view', permission_name: 'Bilgi Bankası Görüntüleme', module_name: 'Bilgi' },
          { permission_code: 'profile_view', permission_name: 'Profil Görüntüleme', module_name: 'Profil' }
        ];
        
      case 'bolum_kullanicisi':
      default:
        return [
          { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
          { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'data_collection_update', permission_name: 'Veri Toplama Güncelleme', module_name: 'Veri Toplama' },
          { permission_code: 'reports_view', permission_name: 'Raporlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'profile_view', permission_name: 'Profil Görüntüleme', module_name: 'Profil' }
        ];
    }
  };

  const hasPermission = (permissionCode: string): boolean => {
    return permissions.some(p => p.permission_code === permissionCode);
  };

  const refreshPermissions = async () => {
    if (currentFirma?.role) {
      await fetchPermissions(currentFirma.role);
    }
  };

  const checkTempPasswordExpiry = () => {
    const tempSession = localStorage.getItem('temp_user_session');
    if (tempSession && tempSession !== 'null' && tempSession !== 'undefined') {
      try {
        const sessionData = JSON.parse(tempSession);
        const createdAt = sessionData.created_at ? new Date(sessionData.created_at) : new Date();
        const expiryDate = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
        const now = new Date();
        const daysRemaining = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysRemaining <= 7 && daysRemaining > 0) {
          const lastWarning = localStorage.getItem('temp_password_warning_dismissed');
          const today = new Date().toDateString();
          
          if (lastWarning !== today) {
            setTempPasswordWarning({ show: true, daysRemaining });
          }
        }
      } catch (error) {
        debugWarn('temp password expiry check error', error);
      }
    } else {
      setTempPasswordWarning({ show: false, daysRemaining: 0 });
    }
  };

  const dismissTempPasswordWarning = () => {
    setTempPasswordWarning({ show: false, daysRemaining: 0 });
    localStorage.setItem('temp_password_warning_dismissed', new Date().toDateString());
  };
  
  // Smart session check kontrolü
  const shouldCheckSession = () => {
    const customToken = localStorage.getItem('custom_auth_token');
    const tempSession = localStorage.getItem('temp_user_session');
    return !customToken && !tempSession;
  };

  // Session refresh retry state
  const [sessionRefreshCount, setSessionRefreshCount] = useState(0);
  const MAX_SESSION_REFRESH = 3;

  // Session health check
  const checkSessionHealth = async () => {
    // Smart check - sadece gerekli durumlarda çalıştır
    if (!shouldCheckSession()) {
      return true;
    }

    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('🚑 Session health check failed:', error);
        return false;
      }
      
      if (!session && profile) {
        // Custom auth kontrolü - session eksikliği normal
        const customToken = localStorage.getItem('custom_auth_token');
        if (customToken) {
          debugTrace('session missing but custom auth active');
          return true;
        }
        
        // Son aktiviteyi kontrol et
        const lastActivity = localStorage.getItem('lastActivity');
        const now = Date.now();
        const timeSinceLastActivity = lastActivity ? now - parseInt(lastActivity) : 0;
        
        // Son aktiviteden 30 dakika geçmişse session gerçekten expire olmuş
        if (timeSinceLastActivity > 30 * 60 * 1000) {
          // Custom auth kontrolü - temizleme yapma
          const customToken = localStorage.getItem('custom_auth_token');
          if (customToken) {
            debugTrace('session expired but custom auth active, cleanup skipped');
            return true;
          }
          debugWarn('session expired but profile exists, clearing');
          setProfile(null);
          setUserFirmas([]);
          setCurrentFirma(null);
          setPermissions([]);
          setLoading(false);
          setSessionRefreshCount(0);
          return false;
        } else {
          debugTrace('session missing but recent activity, attempting refresh');
          
          // Retry limiti kontrolü
          if (sessionRefreshCount >= MAX_SESSION_REFRESH) {
            debugTrace('max session refresh attempts reached, skipping');
            return false;
          }
          
          try {
            await supabase.auth.refreshSession();
            setSessionRefreshCount(prev => prev + 1);
          } catch (refreshError) {
            debugTrace('session refresh failed', refreshError);
            setSessionRefreshCount(prev => prev + 1);
          }
        }
      } else {
        // Session başarılı, retry sayacını sıfırla
        setSessionRefreshCount(0);
      }
      
      if (session?.user && !profile && !fetchingRef.current) {
        debugTrace('session exists but no profile, fetching');
        await fetchAllUserData(session.user.id, session.user.email, true);
      }
      
      return true;
    } catch (error) {
      console.error('🚑 Session health check error:', error);
      return false;
    }
  };
  
  // Periodic session health check - daha az sıklıkta
  useEffect(() => {
    const healthCheckInterval = setInterval(checkSessionHealth, 60000); // 1 dakika
    return () => clearInterval(healthCheckInterval);
  }, [profile, sessionRefreshCount]);

  // Minimal aktivite takibi - sadece gerekli kontroller
  useEffect(() => {
    const updateActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateActivity();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mousedown', updateActivity, { passive: true });
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mousedown', updateActivity);
    };
  }, []);
  
  // Retry mechanism for failed fetches
  const retryFetchUserData = async (userId: string, userEmail?: string) => {
    if (retryCountRef.current >= maxRetries) {
      console.error('❌ Max retries reached, using emergency profile');
      const lastKnownRole = localStorage.getItem(`lastRole_${userId}`);
      const emergencyProfile: Profile = {
        id: userId,
        email: userEmail,
        tam_adi: userEmail || 'Emergency User',
        rol: (lastKnownRole || 'firma_super_admin') as UserRole,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setProfile(emergencyProfile);
      setLoading(false);
      return;
    }
    
    retryCountRef.current++;
    debugTrace(`Retrying user data fetch (${retryCountRef.current}/${maxRetries})`);
    
    setTimeout(() => {
      fetchAllUserData(userId, userEmail, true);
    }, 1000 * retryCountRef.current); // Progressive delay
  };

  useEffect(() => {
    let mounted = true;
    debugTrace('AuthContext useEffect started', mounted);
    
    // Custom auth için özel permissions - ÖNCE tanımla
    const getCustomAuthPermissions = (role: string): UserPermission[] => {
      switch (role) {
        case 'ekostrateji_super_admin':
        case 'firma_super_admin':
          return [
            { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
            { permission_code: 'admin_panel_access', permission_name: 'Yönetim Paneli Erişimi', module_name: 'Yönetim' },
            { permission_code: 'topic_selection_view', permission_name: 'Konu Seçimi Görüntüleme', module_name: 'Konu Yönetimi' },
            { permission_code: 'topic_assignment_view', permission_name: 'Konu Atama Görüntüleme', module_name: 'Konu Yönetimi' },
            { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
            { permission_code: 'survey_preparation_view', permission_name: 'Anket Hazırlama Görüntüleme', module_name: 'Anket' },
            { permission_code: 'materiality_matrix_view', permission_name: 'Çifte Önemlilik Matrisi Görüntüleme', module_name: 'Analiz' },
            { permission_code: 'tsrs_view', permission_name: 'TSRS Görüntüleme', module_name: 'Raporlama' },
            { permission_code: 'user_management', permission_name: 'Kullanıcı Yönetimi', module_name: 'Yönetim' },
            { permission_code: 'role_management', permission_name: 'Rol Yönetimi', module_name: 'Yönetim' },
            { permission_code: 'permission_management', permission_name: 'Permission Yönetimi', module_name: 'Yönetim' },
            { permission_code: 'activity_tracking', permission_name: 'Aktivite Takibi', module_name: 'Yönetim' },
          ];
        case 'bolum_kullanicisi':
          return [
            { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
            { permission_code: 'topic_selection_view', permission_name: 'Konu Seçimi Görüntüleme', module_name: 'Konu Yönetimi' },
            { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
            { permission_code: 'survey_preparation_view', permission_name: 'Anket Hazırlama Görüntüleme', module_name: 'Anket' },
            { permission_code: 'materiality_matrix_view', permission_name: 'Çifte Önemlilik Matrisi Görüntüleme', module_name: 'Analiz' },
            { permission_code: 'tsrs_view', permission_name: 'TSRS Görüntüleme', module_name: 'Raporlama' },
          ];
        default:
          return [];
      }
    };
    
    // Custom auth kontrolü - öncelikli
    const checkCustomAuth = async () => {
      try {
        debugTrace('checkCustomAuth started');
        const token = localStorage.getItem('custom_auth_token');
        const userStr = localStorage.getItem('custom_auth_user');
        
        debugTrace('localStorage check', {
          token: token ? 'var' : 'yok',
          userStr: userStr ? 'var' : 'yok'
        });
        
        if (token && userStr) {
          debugLog('custom auth token and user found');
          const user = JSON.parse(userStr);
          debugTrace('parsed user', user);
          
          if (user && mounted) {
            debugLog('custom auth detected', user.email, user.rol);
            
            // Oturum izlemeyi başlat
            if (user.id) {
              debugTrace('sessionManager.createSession call', user.id);
              const sessionCreated = await sessionManager.createSession(user.id);
              debugTrace('session creation result', sessionCreated);
            } else {
              debugWarn('user.id missing, session cannot be created');
            }
            
            // Kullanıcı değiştiğinde cache temizle
            if (lastFetchedUser.current && lastFetchedUser.current !== user.id) {
              profileCache.current.clear();
            }
            lastFetchedUser.current = user.id;
            
            // Session set et
            setSession({ user: { id: user.id, email: user.email } } as any);
      setProfile(user as Profile);
            
            // Persistent session'a kaydet - tam veri
            persistentSessionManager.save({
              user: { id: user.id, email: user.email, rol: user.rol },
              profile: user,
              permissions: getCustomAuthPermissions(user.rol),
              timestamp: Date.now()
            });
            
            // UserFirmas set et - Süper adminler için tüm firmaları yükle
            if (user.rol === 'ekostrateji_super_admin' || user.rol === 'firma_super_admin') {
              // Async işlemi ayrı fonksiyonda yap
              const loadAllFirmas = async () => {
                try {
                  // Ekostrateji Süper Admin için tüm firmaları yükle
                  if (user.rol === 'ekostrateji_super_admin') {
                    const { data: allFirmalar, error: firmaError } = await supabase
                      .from('firmalar')
                      .select('id, firma_adi')
                      .eq('is_active', true)
                      .order('firma_adi');
                    
                    if (!firmaError && allFirmalar) {
                      const firmas = allFirmalar.map(f => ({
                        firma_id: f.id,
                        firma_adi: f.firma_adi,
                        role: user.rol,
                        is_active: true
                      }));
                      
                      debugLog('super admin firms loaded', firmas.length, firmas.map(f => f.firma_adi));
                      setUserFirmas(firmas);
                      setCurrentFirma(firmas[0] || null);
                      
                      // Persistent session'a kaydet
                      persistentSessionManager.save({
                        user: { id: user.id, email: user.email },
                        profile: user,
                        userFirmas: firmas,
                        selectedFirma: firmas[0] ? {
                          id: firmas[0].firma_id,
                          firma_adi: firmas[0].firma_adi,
                          label: firmas[0].firma_adi
                        } : null,
                        permissions: getCustomAuthPermissions(user.rol)
                      });
                      return;
                    }
                  } else {
                    // Firma Süper Admin için user_firma_relations'dan yükle
                    const { data: userFirmaData, error: ufError } = await supabase
                      .from('user_firma_relations')
                      .select('firma_id')
                      .eq('user_id', user.id)
                      .eq('is_active', true);
                    
                    if (!ufError && userFirmaData && userFirmaData.length > 0) {
                      const firmaIds = userFirmaData.map(uf => uf.firma_id);
                      const { data: firmaData } = await supabase
                        .from('firmalar')
                        .select('id, firma_adi')
                        .in('id', firmaIds)
                        .order('firma_adi');
                      
                      if (firmaData) {
                        const firmas = firmaData.map(f => ({
                          firma_id: f.id,
                          firma_adi: f.firma_adi,
                          role: user.rol,
                          is_active: true
                        }));
                        
                        debugLog('firma super admin firms loaded', firmas.length);
                        setUserFirmas(firmas);
                        setCurrentFirma(firmas[0] || null);
                        
                        persistentSessionManager.save({
                          user: { id: user.id, email: user.email },
                          profile: user,
                          userFirmas: firmas,
                          selectedFirma: firmas[0] ? {
                            id: firmas[0].firma_id,
                            firma_adi: firmas[0].firma_adi,
                            label: firmas[0].firma_adi
                          } : null,
                          permissions: getCustomAuthPermissions(user.rol)
                        });
                        return;
                      }
                    }
                  }
                  
                  // Fallback
                  throw new Error('Firmalar yüklenemedi');
                } catch (error) {
                  console.error('Firmalar yüklenirken hata:', error);
                  const firmas = [{
                    firma_id: user.firma_id || 'default',
                    firma_adi: user.firma_adi || 'Default Firma',
                    role: user.rol,
                    is_active: true
                  }];
                  setUserFirmas(firmas);
                  setCurrentFirma(firmas[0]);
                }
              };
              loadAllFirmas();
            } else {
              const firmas = [{
                firma_id: user.firma_id || 'default',
                firma_adi: user.firma_adi || 'Test Firma',
                role: user.rol,
                is_active: true
              }];
              setUserFirmas(firmas);
              setCurrentFirma(firmas[0]);
            }
            
            // Permissions set et - custom_auth_user'dan çek
            try {
              if (user.permissions && user.permissions.length > 0) {
                setPermissions(user.permissions);
                debugLog('custom_auth_user permissions loaded:', user.permissions.length);
              } else {
                debugWarn('custom_auth_user has no permissions, using fallback');
                const customPermissions = getCustomAuthPermissions(user.rol);
                setPermissions(customPermissions);
                debugLog('custom auth permissions loaded:', customPermissions.length);
              }
            } catch (permError) {
              console.error('❌ Permissions yükleme hatası:', permError);
              const customPermissions = getCustomAuthPermissions(user.rol);
              setPermissions(customPermissions);
              debugLog('custom auth permissions loaded (fallback):', customPermissions.length);
            }
            
            setLoading(false);
            return true;
          }
        }
      } catch (error) {
        debugWarn('custom auth check error', error);
      }
      return false;
    };
    
    // Temp session kontrol et
    const checkTempSession = () => {
      try {
        const tempSession = localStorage.getItem('temp_user_session');
        if (tempSession && tempSession !== 'null' && tempSession !== 'undefined') {
          try {
            const sessionData = JSON.parse(tempSession);
            if (sessionData.expires_at > Date.now()) {
              if (mounted) {
                setSession({ user: sessionData.user } as any);
                setProfile(sessionData.profile);
                
                if (sessionData.userFirmas) {
                  setUserFirmas(sessionData.userFirmas);
                  setCurrentFirma(sessionData.userFirmas[0]);
                  const role = sessionData.userFirmas[0]?.role || sessionData.profile?.rol || 'firma_super_admin';
                  setPermissions(getFallbackPermissions(role));
                } else if (sessionData.permissions) {
                  setPermissions(sessionData.permissions);
                }
                
                setLoading(false);
              }
              return true;
            } else {
              localStorage.removeItem('temp_user_session');
            }
          } catch (error) {
            localStorage.removeItem('temp_user_session');
          }
        }
      } catch (error) {
        debugWarn('temp session check error', error);
      }
      return false;
    };
    
    // Subscription ref
    let subscription: any = null;
    
    // Async initialization
    (async () => {
      debugTrace('AuthContext init IIFE started');
      
      // Custom auth kontrolü - ÖNCE çalıştır (persistent session'dan önce)
      debugTrace('checkCustomAuth about to run');
      try {
        const customAuthResult = await checkCustomAuth();
        debugTrace('checkCustomAuth result', customAuthResult);
        if (customAuthResult) {
          debugLog('custom auth successful, exiting init');
          return;
        }
      } catch (err) {
        console.error('❌ checkCustomAuth hatası:', err);
      }
      
      // Persistent session kontrol et (custom auth başarısız olduysa)
      const persistentSession = persistentSessionManager.restore();
      debugTrace('persistent session check', persistentSession ? 'var' : 'yok');
      if (persistentSession && mounted) {
        debugLog('persistent session restored');
        setSession({ user: persistentSession.user } as any);
        setProfile(persistentSession.profile);
        if (persistentSession.userFirmas) {
          setUserFirmas(persistentSession.userFirmas);
          setCurrentFirma(persistentSession.userFirmas[0] || null);
        }
        if (persistentSession.permissions) {
          setPermissions(persistentSession.permissions);
        }
        setLoading(false);
        return;
      }
      
      // Sonra temp session kontrol et
      if (checkTempSession()) {
        setTimeout(checkTempPasswordExpiry, 1000);
        return;
      }
      
      // Normal Supabase auth
      const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return;
        
        setSession(newSession);
        
        if (event === 'SIGNED_IN' && newSession?.user) {
          await sessionManager.createSession(newSession.user.id);
          if (!fetchingRef.current) {
            await fetchAllUserData(newSession.user.id, newSession.user.email);
          }
        } else if (event === 'SIGNED_OUT') {
          sessionManager.cleanup();
          setProfile(null);
          setUserFirmas([]);
          setCurrentFirma(null);
          setPermissions([]);
          profileCache.current.clear();
          lastFetchedUser.current = null;
          fetchingRef.current = false;
          retryCountRef.current = 0;
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
          if (mounted) setLoading(false);
        } else if (event === 'INITIAL_SESSION' && newSession?.user) {
          await sessionManager.createSession(newSession.user.id);
          if (!fetchingRef.current) {
            await fetchAllUserData(newSession.user.id, newSession.user.email);
          }
        } else {
          if (mounted) setLoading(false);
        }
      }
    );
      subscription = authSubscription;
    })();
    
    // localStorage değişikliklerini dinle
    const handleStorageChange = async () => {
      if (mounted) {
        await checkCustomAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Manuel localStorage değişikliklerini de dinle
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      if (key === 'custom_auth_token' || key === 'custom_auth_user') {
        setTimeout(() => {
          handleStorageChange();
        }, 100);
      }
    };
    
    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem;
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  const signOut = async () => {
    try {
      // End session tracking
      const currentUserId = session?.user?.id || profile?.id;
      if (currentUserId) {
        sessionManager.endSession(currentUserId);
      }
      
      // Clear cache and refs
      profileCache.current.clear();
      lastFetchedUser.current = null;
      retryCountRef.current = 0;
      
      // Custom auth temizle
      localStorage.removeItem('custom_auth_token');
      localStorage.removeItem('custom_auth_user');
      localStorage.removeItem('temp_user_session');
      localStorage.removeItem('user_passwords');
      
      // Persistent session temizle - ama firma bilgilerini koru
      const persistentSession = persistentSessionManager.restore();
      if (persistentSession) {
        persistentSessionManager.save({
          selectedFirma: persistentSession.selectedFirma,
          userFirmas: persistentSession.userFirmas
        });
      }
      
      // Remember me bilgilerini koru
      const rememberMe = localStorage.getItem('rememberMe');
      const rememberedEmail = localStorage.getItem('rememberedEmail');
      
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Remember me bilgilerini geri koy
      if (rememberMe) localStorage.setItem('rememberMe', rememberMe);
      if (rememberedEmail) localStorage.setItem('rememberedEmail', rememberedEmail);
      
      // Clear state
      setLoading(false);
      setProfile(null);
      setUserFirmas([]);
      setCurrentFirma(null);
      setPermissions([]);
      setTempPasswordWarning({ show: false, daysRemaining: 0 });
      
      debugLog('sign out completed (custom auth)');
    } catch (error) {
      console.error('Sign out error:', error);
      // Clear state even if signOut fails
      setLoading(false);
      setProfile(null);
      setUserFirmas([]);
      setCurrentFirma(null);
      setPermissions([]);
      
      // Force redirect
      setTimeout(() => {
        window.location.href = '/auth/login';
      }, 100);
    }
  };

  const value = useMemo(() => ({
    user: session?.user ?? null,
    session,
    profile,
    userFirmas,
    currentFirma,
    permissions,
    loading,
    hasPermission,
    switchFirma,
    signOut,
    refreshPermissions,
    tempPasswordWarning,
    dismissTempPasswordWarning
  }), [session, profile, userFirmas, currentFirma, permissions, loading, tempPasswordWarning]);
  
  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : (
        <div className="min-h-screen bg-gray-50">
          <div className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-4">
                <div className="h-8 w-32 bg-gray-200 rounded animate-pulse"></div>
                <div className="h-8 w-24 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
          
          <div className="flex">
            <div className="w-64 bg-white shadow-sm border-r min-h-screen">
              <div className="p-4 space-y-3">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-10 bg-gray-200 rounded animate-pulse"></div>
                ))}
              </div>
            </div>
            
            <div className="flex-1 p-6">
              <div className="space-y-6">
                <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
                <div className="grid grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-32 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>
              </div>
              
              <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Oturum yükleniyor...</span>
                  <button 
                    onClick={signOut}
                    className="ml-2 px-2 py-1 bg-red-500 hover:bg-red-600 rounded text-xs transition-colors"
                    title="Zorla Çıkış"
                  >
                    Çıkış
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
